import React, { useState, useEffect } from 'react';
import { Calendar } from './components/Calendar';
import { Dashboard } from './components/Dashboard';
import { WeeklySummary } from './components/WeeklySummary';
import { DayPopup } from './components/DayPopup';
import { ResourceLibrary } from './components/ResourceLibrary';
import { CalendarState, DayData, WeekProgress } from './types';
import { generateDailyTasks, getWeekNumber } from './data/studyPlan';
import { formatDateKey } from './utils/dateUtils';
import { loadFromLocalStorage, saveToLocalStorage, calculateStats, saveDayData } from './utils/localStorage';
import { Moon, Sun } from 'lucide-react';
import './App.css';

function App() {
  const [state, setState] = useState<CalendarState>({
    currentMonth: new Date(2025, 6), // July 2025
    selectedDate: null,
    dayData: {},
    isDarkMode: false,
    currentStreak: 0,
    totalProblemsSolved: 0,
    showResourceLibrary: false,
    showCatchUpMode: false
  });

  const [showDayPopup, setShowDayPopup] = useState(false);
  const [currentWeekProgress, setCurrentWeekProgress] = useState<WeekProgress>({
    weekNumber: 1,
    tasksCompleted: 0,
    totalTasks: 0,
    problemsSolved: 0,
    topicsCovered: [],
    mockInterviewsCompleted: 0
  });

  // Load data from localStorage on mount
  useEffect(() => {
    const savedData = loadFromLocalStorage();
    if (savedData) {
      setState(prevState => ({
        ...prevState,
        ...savedData,
        currentMonth: savedData.currentMonth || prevState.currentMonth,
        selectedDate: savedData.selectedDate || null
      }));
    }
  }, []);

  // Calculate stats whenever dayData changes
  useEffect(() => {
    const stats = calculateStats(state.dayData);
    setState(prevState => ({
      ...prevState,
      ...stats
    }));
  }, [state.dayData]);

  // Calculate weekly progress
  useEffect(() => {
    const today = new Date();
    const weekNumber = getWeekNumber(today);
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    
    let tasksCompleted = 0;
    let totalTasks = 0;
    let problemsSolved = 0;
    let mockInterviewsCompleted = 0;
    const topicsCovered = new Set<string>();
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      const dateKey = formatDateKey(date);
      const dayData = state.dayData[dateKey];
      
      if (dayData) {
        dayData.tasks.forEach(task => {
          totalTasks++;
          if (task.completed) {
            tasksCompleted++;
            if (task.category === 'dsa') problemsSolved++;
            if (task.category === 'mock-interview') mockInterviewsCompleted++;
            topicsCovered.add(task.category);
          }
        });
      } else {
        // Generate default tasks for calculation
        const defaultTasks = generateDailyTasks(date);
        totalTasks += defaultTasks.length;
      }
    }
    
    setCurrentWeekProgress({
      weekNumber,
      tasksCompleted,
      totalTasks,
      problemsSolved,
      topicsCovered: Array.from(topicsCovered),
      mockInterviewsCompleted
    });
  }, [state.dayData]);

  const handleMonthChange = (newMonth: Date) => {
    setState(prevState => ({
      ...prevState,
      currentMonth: newMonth
    }));
    saveToLocalStorage({ currentMonth: newMonth });
  };

  const handleDateClick = (date: Date) => {
    const dateKey = formatDateKey(date);
    let dayData = state.dayData[dateKey];
    
    if (!dayData) {
      // Generate default tasks for this day
      const tasks = generateDailyTasks(date);
      dayData = {
        date: dateKey,
        tasks,
        notes: '',
        completed: false
      };
    }
    
    setState(prevState => ({
      ...prevState,
      selectedDate: date,
      dayData: {
        ...prevState.dayData,
        [dateKey]: dayData
      }
    }));
    setShowDayPopup(true);
  };

  const handleDayDataSave = (dayData: DayData) => {
    const dateKey = formatDateKey(state.selectedDate!);
    saveDayData(dateKey, dayData);
    setState(prevState => ({
      ...prevState,
      dayData: {
        ...prevState.dayData,
        [dateKey]: dayData
      }
    }));
  };

  const toggleDarkMode = () => {
    setState(prevState => ({
      ...prevState,
      isDarkMode: !prevState.isDarkMode
    }));
    saveToLocalStorage({ isDarkMode: !state.isDarkMode });
  };

  const weekProgressForDashboard = {
    completed: currentWeekProgress.tasksCompleted,
    total: currentWeekProgress.totalTasks
  };

  return (
    <div className={`app ${state.isDarkMode ? 'dark-mode' : ''}`}>
      <div className="app-header">
        <h1>Interview Prep Calendar</h1>
        <button onClick={toggleDarkMode} className="theme-toggle">
          {state.isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
        </button>
      </div>
      
      <div className="app-content">
        <Dashboard
          totalProblemsSolved={state.totalProblemsSolved}
          currentStreak={state.currentStreak}
          onResourceLibraryClick={() => setState(prev => ({ ...prev, showResourceLibrary: true }))}
          weekProgress={weekProgressForDashboard}
        />
        
        <WeeklySummary weekProgress={currentWeekProgress} />
        
        <Calendar
          currentMonth={state.currentMonth}
          onMonthChange={handleMonthChange}
          onDateClick={handleDateClick}
          dayData={state.dayData}
          selectedDate={state.selectedDate}
        />
      </div>
      
      {showDayPopup && state.selectedDate && (
        <DayPopup
          date={state.selectedDate}
          dayData={state.dayData[formatDateKey(state.selectedDate)] || {
            date: formatDateKey(state.selectedDate),
            tasks: generateDailyTasks(state.selectedDate),
            notes: '',
            completed: false
          }}
          onClose={() => setShowDayPopup(false)}
          onSave={handleDayDataSave}
        />
      )}
      
      {state.showResourceLibrary && (
        <ResourceLibrary
          onClose={() => setState(prev => ({ ...prev, showResourceLibrary: false }))}
        />
      )}
    </div>
  );
}

export default App;
